<template>
  <div
    class="dropdown multi-select-dropdown"
    ref="dropdown"
  >
    <button
      class="btn dropdown-toggle dropdown-button"
      type="button"
      @click="toggleDropdown"
      :aria-expanded="isOpen"
      :style="{ width: buttonWidth }"
    >
      {{ selectedLabels }}
    </button>
    <div
      v-if="isOpen"
      class="dropdown-menu show dropdown-menu-scrollable"
    >
      <input
        type="text"
        v-model="searchQuery"
        class="form-control dropdown-search"
        :style="{ width: buttonWidth }"
        :placeholder="t('common.searchPlaceholder')"
      />
      <div
        v-for="option in filteredOptions"
        :key="option.value"
        class="dropdown-item"
      >
        <div class="form-check">
          <input
            :id="option.value"
            v-model="selectedValues"
            class="form-check-input"
            type="checkbox"
            :value="option.value"
            :disabled="disabledOptions.includes(option.value)"
          />
          <label
            class="form-check-label"
            :for="option.value"
            :class="{ 'text-muted': disabledOptions.includes(option.value) }"
          >
            {{ option.label }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  modelValue: {
    type: Array,
    required: true,
    default: () => [],
  },
  buttonWidth: {
    type: String,
    required: false,
    default: '100%',
  },
  constLabel: {
    type: String,
    required: false,
    default: '',
  },
  disabledOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'changeTdStyle'])

const isOpen = ref(false)
const searchQuery = ref('')
const selectedValues = ref([...props.modelValue])

const dropdown = ref(null)

const selectedLabels = computed(() => {
  if (props.constLabel !== '') {
    return props.constLabel
  }
  if (selectedValues.value.length === 0) {
    return t('selectOptions')
  }
  return props.options
    .filter(option => selectedValues.value.includes(option.value))
    .map(option => option.label)
    .join('[..]')
})

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option => option.label.toLowerCase().includes(query))
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    searchQuery.value = '' // Сброс строки поиска при закрытии
  }
  emit('changeTdStyle', isOpen.value)
}

const handleClickOutside = event => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false
    emit('changeTdStyle', isOpen.value)
  }
}

watch(selectedValues, newValues => {
  emit('update:modelValue', newValues)
})

watch(
  () => props.modelValue,
  newValues => {
    selectedValues.value = newValues
  }
)

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<style scoped>
.multi-select-dropdown {
  font-size: inherit;
  width: 100%;
  position: relative;
}

.dropdown-menu {
  font-size: inherit;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  border: 1px solid #ccc;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  padding-right: 0.1em;
}

.dropdown-menu-scrollable {
  font-size: inherit;
  max-height: calc(1.5em * 15);
  overflow-y: auto;
  padding-right: 1.5em;
}

.dropdown-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0.75rem;
  font-size: inherit;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-search {
  margin: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: inherit;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: inherit;
  cursor: pointer;
  z-index: 3000;
}

.dropdown-item:hover {
  background-color: #f1f1f1;
  font-size: inherit;
}

.form-check {
  display: flex;
  align-items: center;
}

.form-check-input {
  margin-right: 0.5rem;
  font-size: inherit;
  margin-bottom: 0.2em;
}

.form-check-label {
  font-size: inherit;
}

.text-muted {
  color: #6c757d !important;
}
</style>

<i18n>
{
  "ru": {
    "selectOptions": "Выберите опции"
  },
  "en": {
    "selectOptions": "Select options"
  }
}
</i18n>
